import { h, Component, Event, EventEmitter, Prop, Host } from '@stencil/core';
import state, { store } from '../../state';
import { getViewportValue } from '../../utils';

import type { SuccessContent, SuccessCtaValue, ContentForDevice } from '../../types';

type CtaSlot = 'primary' | 'secondary' | 'tertiary';

const fieldMapping: Record<CtaSlot, 'cta' | 'secondCta' | 'thirdCta'> = {
  primary: 'cta',
  secondary: 'secondCta',
  tertiary: 'thirdCta',
};

@Component({
  tag: 'om-success-screen',
  styleUrls: ['./screens.css', './success-screen.css'],
  scoped: true,
})
export class SuccessScreen {
  @Prop() screenValues: SuccessContent;
  @Prop() unmount?: () => void;

  @Event() layoutContainerClicked: EventEmitter;

  private handleCtaClick(cta: ContentForDevice<SuccessCtaValue> | undefined) {
    if (state.mode === 'preview' || !cta) return;

    const ctaValue = getViewportValue(cta);
    const url = ctaValue?.url;
    const target = ctaValue?.target || '_blank';

    if (url) {
      window.open(url, target);
    }

    if (ctaValue?.closeOverlay) {
      this.unmount?.();
    }
  }

  private renderCta(ctaValue: SuccessCtaValue, cta: ContentForDevice<SuccessCtaValue> | undefined, ctaSlot: CtaSlot) {
    return (
      <om-clickable-element
        class={`cta-wrapper ${ctaSlot}-cta`}
        element={{ field: fieldMapping[ctaSlot] }}
        overlayState={state}
        overlayStore={store}
      >
        <button
          type="button"
          class="success-cta"
          style={{
            '--cta-bg-color': ctaValue.backgroundColour || '#000000',
            '--cta-border-radius': ctaValue.borderRadius || '0',
            '--cta-border-color': ctaValue.borderColour || 'transparent',
            '--cta-border-width': ctaValue.borderWidth || '0',
          }}
          onClick={() => this.handleCtaClick(cta)}
          innerHTML={ctaValue.text}
        />
      </om-clickable-element>
    );
  }

  render() {
    const ctaValue = this.screenValues.cta ? getViewportValue(this.screenValues.cta) : null;
    const secondCtaValue = this.screenValues.secondCta ? getViewportValue(this.screenValues.secondCta) : null;
    const thirdCtaValue = this.screenValues.thirdCta ? getViewportValue(this.screenValues.thirdCta) : null;

    const showCta = !!ctaValue && !ctaValue.shouldHide;
    const showSecondCta = showCta && !!secondCtaValue && !secondCtaValue.shouldHide;
    const showThirdCta = showSecondCta && !!thirdCtaValue && !thirdCtaValue.shouldHide;

    return (
      <Host
        style={{
          ['--textBlockBgColour']: getViewportValue(this.screenValues.textBlockBgColour, 'colour'),
        }}
      >
        <div
          class={state.isMobileViewport ? 'screen-container is-mobile-viewport' : 'screen-container'}
          onClick={this.layoutContainerClicked.emit}
          ref={(el: HTMLDivElement) => {
            state.currentScreenContainer = el as HTMLElement;
          }}
        >
          <div class="content-area">
            <div class="inner-container">
              <om-clickable-element
                innerHTML={getViewportValue(this.screenValues.title)}
                class="title"
                element={{ field: 'title' }}
              />
              <om-clickable-element
                innerHTML={getViewportValue(this.screenValues.body)}
                class="body"
                element={{ field: 'body' }}
              />
              {(showCta || showSecondCta || showThirdCta) && (
                <div class="cta-section">
                  {showCta && this.renderCta(ctaValue, this.screenValues.cta, 'primary')}
                  {showSecondCta && this.renderCta(secondCtaValue, this.screenValues.secondCta, 'secondary')}
                  {showThirdCta && this.renderCta(thirdCtaValue, this.screenValues.thirdCta, 'tertiary')}
                </div>
              )}
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
